import { useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ChevronRight, MessageCircle, ArrowRight, CheckCircle2, ChevronLeft, X } from "lucide-react";
import { getProduct, PRODUCTS } from "../data/products";
import { Reveal } from "../components/Reveal";
import { SITE, waLink } from "../lib/site";
import { Dialog, DialogContent } from "../components/ui/dialog";

export default function ProductDetail() {
  const { slug } = useParams();
  const product = getProduct(slug);
  const [activeImg, setActiveImg] = useState(0);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIdx, setGalleryIdx] = useState(0);

  if (!product) return <Navigate to="/products" replace />;

  const related = PRODUCTS.filter((p) => p.category === product.category && p.slug !== product.slug).slice(0, 3);

  const handlePrevImage = () => {
    setGalleryIdx((prev) => (prev === 0 ? product.images.length - 1 : prev - 1));
  };

  const handleNextImage = () => {
    setGalleryIdx((prev) => (prev === product.images.length - 1 ? 0 : prev + 1));
  };

  return (
    <div data-testid="page-product-detail">
      {/* Breadcrumb */}
      <div className="bg-brand-surface border-b border-brand-line">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-2 text-xs text-brand-ink/65 font-heading font-semibold uppercase tracking-wider">
          <Link to="/" className="hover:text-brand-orange">Home</Link>
          <ChevronRight size={12} />
          <Link to="/products" className="hover:text-brand-orange">Products</Link>
          <ChevronRight size={12} />
          <span className="text-brand-ink">{product.name}</span>
        </div>
      </div>

      <section className="bg-white py-14">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
          {/* Images */}
          <div>
            <button
              onClick={() => {
                setGalleryIdx(activeImg);
                setGalleryOpen(true);
              }}
              className="w-full aspect-square bg-brand-surface rounded-lg overflow-hidden border border-brand-line cursor-pointer transition-all hover:border-brand-orange group"
              data-testid="product-main-image"
            >
              <img src={product.images[activeImg]} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
            </button>
            {product.images.length > 1 && (
              <div className="grid grid-cols-4 gap-2 mt-3">
                {product.images.map((src, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImg(i)}
                    data-testid={`product-thumb-${i}`}
                    className={`aspect-square rounded-md overflow-hidden border-2 transition-all cursor-pointer group ${activeImg === i ? "border-brand-orange" : "border-brand-line hover:border-brand-ink/40"}`}
                  >
                    <img src={src} alt={`${product.name} ${i + 1}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div>
            <div className="flex items-center gap-3 mb-3">
              <span className="text-[10px] font-heading font-bold tracking-[0.18em] uppercase text-brand-orange px-2 py-1 rounded bg-brand-orange/10">{product.categoryLabel}</span>
              <span className="text-[10px] font-heading font-bold tracking-wider uppercase px-2 py-1 rounded bg-brand-green/10 text-brand-green">In Stock</span>
            </div>
            <h1 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl text-brand-ink tracking-tight uppercase mb-3 leading-tight">{product.name}</h1>
            <p className="text-base text-brand-ink/70 mb-7 leading-relaxed">{product.tagline}</p>

            {/* Composition */}
            <Reveal>
              <div className="mb-7">
                <h3 className="font-heading font-bold text-xs uppercase tracking-[0.18em] text-brand-orange mb-2">Composition</h3>
                <p className="text-sm text-brand-ink/80 leading-relaxed">{product.composition}</p>
              </div>
            </Reveal>

            {/* Indications */}
            <Reveal>
              <div className="mb-7">
                <h3 className="font-heading font-bold text-xs uppercase tracking-[0.18em] text-brand-orange mb-3">Indications / Benefits</h3>
                <ul className="space-y-2">
                  {product.indications.map((ind, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-brand-ink/80">
                      <CheckCircle2 size={16} className="text-brand-green mt-0.5 shrink-0" />
                      <span>{ind}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>

            {/* Dosage */}
            <Reveal>
              <div className="mb-7">
                <h3 className="font-heading font-bold text-xs uppercase tracking-[0.18em] text-brand-orange mb-3">Dosage &amp; Administration</h3>
                <div className="overflow-hidden border border-brand-line rounded-md">
                  <table className="w-full text-sm" data-testid="dosage-table">
                    <tbody>
                      {product.dosage.map(([k, v], i) => (
                        <tr key={i} className={i % 2 === 0 ? "bg-brand-surface" : "bg-white"}>
                          <td className="px-4 py-2.5 font-heading font-semibold text-brand-ink w-1/2 border-b border-brand-line">{k}</td>
                          <td className="px-4 py-2.5 text-brand-ink/80 border-b border-brand-line">{v}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </Reveal>

            {/* Presentation */}
            <div className="mb-8">
              <h3 className="font-heading font-bold text-xs uppercase tracking-[0.18em] text-brand-orange mb-2">Presentation / Pack Sizes</h3>
              <p className="text-sm text-brand-ink/80">{product.pack}</p>
            </div>

            {/* CTAs */}
            <div className="flex flex-wrap gap-3">
              <Link to={`/contact?product=${encodeURIComponent(product.name)}`} data-testid="product-inquiry-cta" className="inline-flex items-center gap-2 bg-brand-orange hover:bg-brand-orangeDark text-white font-heading font-semibold text-sm px-6 py-3.5 rounded-md transition-colors">
                Send Inquiry <ArrowRight size={16} />
              </Link>
              <a href={waLink(`Hello Desire Vet Care, I'm interested in ${product.name}. Please share more details.`)} target="_blank" rel="noreferrer" data-testid="product-wa-cta" className="inline-flex items-center gap-2 bg-brand-green hover:bg-brand-greenDark text-white font-heading font-semibold text-sm px-6 py-3.5 rounded-md transition-colors">
                <MessageCircle size={16} /> WhatsApp
              </a>
            </div>

            <div className="mt-8 pt-6 border-t border-brand-line text-xs text-brand-ink/60">
              For dosage adjustments &amp; specific case advice, consult your veterinarian. Call us at <a href={`tel:${SITE.phoneRaw}`} className="text-brand-orange font-semibold">{SITE.phoneDisplay}</a>.
            </div>
          </div>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="bg-brand-surface py-16">
          <div className="max-w-7xl mx-auto px-6">
            <div className="mb-8">
              <div className="text-brand-orange font-heading font-bold text-xs tracking-[0.22em] uppercase mb-2">Related Products</div>
              <h2 className="font-heading font-extrabold text-3xl text-brand-ink tracking-tight">You may also need</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {related.map((p) => (
                <Link key={p.slug} to={`/products/${p.slug}`} data-testid={`related-${p.slug}`} className="product-card block bg-white border border-brand-line rounded-lg overflow-hidden">
                  <div className="aspect-[4/3] bg-brand-surface overflow-hidden">
                    <img src={p.images[0]} alt={p.name} loading="lazy" className="w-full h-full object-cover" />
                  </div>
                  <div className="p-5">
                    <div className="text-[10px] font-heading font-bold tracking-[0.18em] uppercase text-brand-orange mb-1.5">{p.categoryLabel}</div>
                    <h3 className="font-heading font-bold text-base uppercase tracking-wide text-brand-ink leading-snug">{p.name}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Image Gallery Modal */}
      <Dialog open={galleryOpen} onOpenChange={setGalleryOpen}>
        <DialogContent className="w-screen h-screen max-w-none max-h-none p-0 border-0 bg-black/90 overflow-visible">
          {/* Close Button */}
          <button
            onClick={() => setGalleryOpen(false)}
            className="absolute top-6 right-6 z-50 bg-red-600 hover:bg-red-700 text-white p-2 rounded-lg transition-all duration-200 flex items-center justify-center w-10 h-10"
            aria-label="Close gallery"
          >
            <X size={24} strokeWidth={3} />
          </button>

          <div className="flex items-center justify-center h-full w-full relative">
            {/* Main Image */}
            <div className="flex-1 flex items-center justify-center px-20">
              <img 
                src={product.images[galleryIdx]} 
                alt={`${product.name} ${galleryIdx + 1}`}
                className="max-w-full max-h-[85vh] object-contain"
              />
            </div>

            {/* Navigation Arrows */}
            {product.images.length > 1 && (
              <>
                <button
                  onClick={handlePrevImage}
                  className="fixed left-8 top-1/2 -translate-y-1/2 z-50 bg-white/40 hover:bg-white/60 text-white p-4 rounded-full transition-all duration-200 shadow-lg"
                  aria-label="Previous image"
                >
                  <ChevronLeft size={48} strokeWidth={3} />
                </button>
                <button
                  onClick={handleNextImage}
                  className="fixed right-8 top-1/2 -translate-y-1/2 z-50 bg-white/40 hover:bg-white/60 text-white p-4 rounded-full transition-all duration-200 shadow-lg"
                  aria-label="Next image"
                >
                  <ChevronRight size={48} strokeWidth={3} />
                </button>

                {/* Image Counter */}
                <div className="fixed bottom-24 left-1/2 -translate-x-1/2 bg-black/80 text-white px-6 py-3 rounded-full text-lg font-bold z-40">
                  {galleryIdx + 1} / {product.images.length}
                </div>

                {/* Thumbnail Strip */}
                <div className="fixed bottom-4 left-0 right-0 flex justify-center gap-3 overflow-x-auto px-4 z-40 pb-2">
                  {product.images.map((src, i) => (
                    <button
                      key={i}
                      onClick={() => setGalleryIdx(i)}
                      className={`h-20 w-20 rounded-lg overflow-hidden flex-shrink-0 border-3 transition-all hover:scale-110 ${
                        galleryIdx === i ? "border-brand-orange shadow-lg shadow-brand-orange" : "border-white/50 hover:border-white"
                      }`}
                    >
                      <img src={src} alt={`Thumbnail ${i + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
